import React from 'react'
import moment from 'moment'
import { Text, TextColor } from '@sendoutcards/quantum-design-ui'
// @src imports
import { useSubscriptions } from 'src/hooks'
import { Button, Icon } from 'src/chrome'
import { dollarsAndCents } from 'src/helpers'
import { PlanFragment, StripeSubscription } from 'src/graphql'
import { Plan, PlanDetails } from 'src/marketing/plans/types'
import { PLAN_ORDER } from 'src/marketing/plans/constants'
import { DecoratedPlan } from 'src/redux/selectors/plans'

import { colors, qdsColors, style } from './pricingTableStyles'

type PlanTileProps = {
  plan: Plan | PlanFragment | DecoratedPlan
  details?: PlanDetails
  containerStyle?: React.CSSProperties
  onSelect: (plan: string, isKeepButton?: boolean) => void
  stripeSubscription?: Partial<StripeSubscription>
  getPlan: (planId: string) => PlanFragment | undefined
}

const PlanTile: React.FC<PlanTileProps> = props => {
  const { plan, details, onSelect, containerStyle, stripeSubscription } = props
  const { currentPlan, newPlan, newPlanStripeId } = useSubscriptions()
  const isCurrentPlan = currentPlan.id === plan.id
  const amount = dollarsAndCents(plan.price.amount ?? 0)
  const primary = (!isCurrentPlan && plan.color && plan.color !== 'red'
    ? qdsColors.primary[plan.color as keyof typeof qdsColors.primary]
    : qdsColors.white) as TextColor
  const primaryPlan =
    !isCurrentPlan && plan.color
      ? colors.primary[plan.color as keyof typeof colors.primary]
      : colors.white
  const secondary: string =
    !isCurrentPlan && plan.color
      ? colors.secondary[plan.color as keyof typeof colors.secondary]
      : colors.white

  const qDSColorOverride = {
    '*': {
      color: primaryPlan,
    },
  }

  const tileBackground: string = isCurrentPlan ? colors.active : colors.white
  const isNewPlan = newPlanStripeId === plan.stripeId

  const isDowngraded =
    newPlan &&
    PLAN_ORDER.indexOf(newPlan.stripeId) <
      PLAN_ORDER.indexOf(currentPlan.stripeId)

  const isPreviousPlan = isDowngraded && isCurrentPlan

  return (
    <div
      style={{
        background: tileBackground,
        ...style.planHeader,
        ...containerStyle,
      }}
    >
      {isCurrentPlan && (
        <div style={style.chip}>
          <Text type="caption" color="success" alignment="center">
            Current Plan
          </Text>
          <Text type="footnote" color="success" alignment="center">
            until{' '}
            {moment(stripeSubscription?.currentPeriodEnd).format('MMMM Do')}
          </Text>
        </div>
      )}
      {isNewPlan && (
        <div style={style.chip}>
          <Text type="caption" color="success" alignment="center">
            New Plan
          </Text>
          <Text type="footnote" color="success" alignment="center">
            on {moment(stripeSubscription?.currentPeriodEnd).format('MMMM Do')}
          </Text>
        </div>
      )}
      <div
        style={{
          ...style.titleBlock,
          borderBottom: `.5px solid ${secondary}`,
        }}
      >
        <div css={qDSColorOverride}>
          <Text type="subtitle" color={primary}>
            {plan.title.toUpperCase()}
          </Text>
        </div>
      </div>
      <div style={style.amountBlock}>
        <div style={{ display: 'flex', paddingTop: '24px' }}>
          <div style={{ marginTop: -8 }}>
            <Icon icon={'DOLLARSIGN'} size={18} color={secondary} />
          </div>
          <div css={qDSColorOverride}>
            <Text type="heroTitle" color={primary}>
              {amount.dollars}
            </Text>
          </div>
          <div css={qDSColorOverride} style={{ marginTop: '40px' }}>
            <Text type="subtitle" color={primary} outset={{ top: 'auto' }}>
              {`.${amount.cents}`}
            </Text>
          </div>
        </div>
        <div css={qDSColorOverride}>
          <Text type="body" color={primary}>
            / month
          </Text>
        </div>
        {details && (
          <ul style={{ marginTop: 15, fontSize: 14 }}>
            {details.details &&
              details.details.map(detail => (
                <li
                  key={detail.detail}
                  style={{
                    ...{ listStyleType: 'none', padding: '3px 0' },
                    ...(isCurrentPlan
                      ? {
                          listStyleType: 'none',
                          color: primary,
                          fontWeight: 600,
                        }
                      : { listStyleType: 'none', color: 'rgba(0,0,0,.55)' }),
                  }}
                >
                  {detail.innerDetails && (
                    <>
                      <Text
                        type="footnote"
                        color={isCurrentPlan ? primary : 'primaryBody'}
                        weight="bold"
                      >
                        {detail.detail}
                      </Text>
                      <Text
                        type="footnote"
                        color={isCurrentPlan ? primary : 'primaryBody'}
                        weight="bold"
                      >
                        (postage not included)
                      </Text>
                      <ul>
                        {detail.innerDetails.map(innerDetail => (
                          <li
                            key={innerDetail.detail}
                            style={{
                              ...{ listStyleType: 'none', padding: '3px 0' },
                              ...(isCurrentPlan
                                ? {
                                    listStyleType: 'none',
                                    color: primary,
                                    fontWeight: 600,
                                  }
                                : {
                                    listStyleType: 'none',
                                    color: 'rgba(0,0,0,.55)',
                                  }),
                            }}
                          >
                            <Text
                              type="footnote"
                              color={isCurrentPlan ? primary : 'primaryBody'}
                              inset={{ left: 'x1_5' }}
                            >
                              {innerDetail.detail}
                            </Text>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {!detail.innerDetails && (
                    <Text
                      type="footnote"
                      color={isCurrentPlan ? primary : 'primaryBody'}
                    >
                      {detail.detail}
                    </Text>
                  )}
                </li>
              ))}
          </ul>
        )}
        {!isNewPlan && !isCurrentPlan && (
          <Button
            id={`add_${plan.title.toLowerCase()}_btn`}
            title="select"
            onClick={() => onSelect(plan.title)}
            style={{
              ...style.selectButton,
            }}
          />
        )}
        {isPreviousPlan && (
          <Button
            id={`keep_plan_btn`}
            title="Keep"
            onClick={() => onSelect(plan.title, true)}
            style={{
              ...style.selectButton,
            }}
          />
        )}
        {isCurrentPlan &&
          plan.title !== 'Basic' &&
          stripeSubscription &&
          !stripeSubscription.isSetToCancel && (
            <div
              style={{
                height: '30px',
                position: 'absolute',
                bottom: '0px',
                right: '15px',
                cursor: 'pointer',
              }}
              onClick={() => onSelect('Basic')}
            >
              <Text type="body" color="accent" alignment="center">
                Cancel
              </Text>
            </div>
          )}
      </div>
    </div>
  )
}

export default PlanTile
